<template>
  <!-- 문헌 수집 — 데이터 탐색(검색·시각화). 현재 버전 행을 논문 경계 없이 검색하고 분포·산점도·건수로 본다. (보기 UI, 2026-09-27 결정) -->
  <div class="lit-page">
    <div class="title_cont">
      <h2 class="title_txt">문헌 수집 — 데이터 탐색</h2>
    </div>
    <div class="ptc_container">
      <router-link to="/literature" class="lit-back">← 논문 목록</router-link>

      <!-- 현황 카드 -->
      <div class="lit-cards" v-if="stats">
        <div class="lit-card"><div class="lab">논문</div><div class="val">{{ n(stats.papers) }}</div><div class="sub">추출 있음 {{ n(stats.papers_with_extraction) }} · 원문 {{ n(stats.papers_with_fulltext) }}</div></div>
        <div class="lit-card"><div class="lab">추출 행</div><div class="val">{{ n(stats.rows) }}</div><div class="sub">값 있음 {{ n(stats.rows_with_value) }} · 검토필요 {{ n(stats.rows_needs_review) }}</div></div>
        <div class="lit-card"><div class="lab">업로드 버전</div><div class="val">{{ n(stats.uploads) }}</div><div class="sub">현재 버전 기준</div></div>
        <div class="lit-card wide">
          <div class="lab">물성 상위</div>
          <div class="props">
            <button v-for="p in (stats.properties || []).slice(0, 12)" :key="p.property" type="button" class="lit-chip small" :class="{ on: filter.property === p.property }" @click="pickProperty(p.property)">
              {{ p.property }} <b>{{ p.count }}</b>
            </button>
          </div>
        </div>
      </div>

      <!-- 조건 -->
      <div class="lit-filter">
        <label>검색 <input type="text" v-model="filter.q" placeholder="물성·조성·근거 문장·제목" @keyup.enter="run"></label>
        <label>분야
          <select v-model="filter.group"><option value="">전체</option><option v-for="g in groups" :key="g.code" :value="g.code">{{ g.label }}</option></select>
        </label>
        <label>물성
          <input list="lit-prop-list" v-model="filter.property" placeholder="정확한 물성명" @change="onPropertyChange">
          <datalist id="lit-prop-list"><option v-for="p in propertyOptions" :key="p.property" :value="p.property">{{ p.count }}행</option></datalist>
        </label>
        <label>단위
          <select v-model="filter.unit"><option value="">전체</option><option v-for="u in unitOptions" :key="u" :value="u">{{ u }}</option></select>
        </label>
        <label>값 <input type="number" v-model="filter.valueMin" placeholder="최소" class="num"> ~ <input type="number" v-model="filter.valueMax" placeholder="최대" class="num"></label>
        <label>학교
          <select v-model="filter.institution"><option value="">전체</option><option v-for="i in (stats ? stats.by_institution : [])" :key="i.key" :value="i.key">{{ i.label }} ({{ i.count }})</option></select>
        </label>
        <label class="lit-check"><input type="checkbox" v-model="filter.excludeCitation"> 인용값 제외</label>
        <label class="lit-check"><input type="checkbox" v-model="filter.excludeModel"> 모델값 제외</label>
        <label class="lit-check"><input type="checkbox" v-model="filter.needsReview"> 검토필요만</label>
        <label class="lit-check"><input type="checkbox" v-model="filter.valueOnly"> 값 있는 행만</label>
        <button type="button" class="sm_btn" @click="run">조회</button>
        <button type="button" class="sm_btn" @click="reset">초기화</button>
      </div>

      <!-- 뷰 탭 -->
      <div class="lit-tabs">
        <button type="button" class="lit-tab" :class="{ on: view === 'table' }" @click="view = 'table'">표 <small v-if="table">({{ n(table.total) }}행)</small></button>
        <button type="button" class="lit-tab" :class="{ on: view === 'hist' }" @click="switchView('hist')">분포</button>
        <button type="button" class="lit-tab" :class="{ on: view === 'scatter' }" @click="switchView('scatter')">산점도</button>
        <button type="button" class="lit-tab" :class="{ on: view === 'count' }" @click="switchView('count')">건수</button>
        <div class="lit-tabs-right">
          <button type="button" class="sm_btn" :disabled="exporting || !table || !table.total" @click="exportXlsx">{{ exporting ? '내보내는 중…' : '엑셀 (검색 결과)' }}</button>
        </div>
      </div>

      <!-- 표 -->
      <div v-if="view === 'table'">
        <div class="lit-table-wrap" v-if="table && table.items.length">
          <table class="lit-table">
            <thead><tr><th>논문</th><th>분야</th><th>시편</th><th>물성</th><th>값</th><th>단위</th><th>조건</th><th>위치 · 원문 문장</th><th>표시</th></tr></thead>
            <tbody>
            <tr v-for="r in table.items" :key="r.id" :class="{ review: r.needs_review }">
              <td class="paper"><router-link :to="'/literature/' + r.paper_id" :title="r.title">{{ short(r.title, 60) }}</router-link><div class="lit-sub mono">{{ r.doi }} · v{{ r.version }}</div></td>
              <td>{{ groupLabel(r.group) }}</td>
              <td><span v-if="r.sample_no != null">#{{ r.sample_no }}</span><div class="lit-sub">{{ r.sample_type || '' }}</div><div class="lit-sub">{{ sampleLabel(r) }}</div></td>
              <td>{{ r.property_name }}<div class="lit-sub" v-if="r.property_detail">{{ r.property_detail }}</div></td>
              <td class="mono num">{{ r.value == null ? '—' : r.value }}</td>
              <td>{{ r.unit || '' }}</td>
              <td class="lit-sub">{{ condition(r) }}</td>
              <td class="quote"><div class="lit-sub">{{ r.source_section || '' }}</div>{{ short(r.evidence_quote, 160) }}</td>
              <td class="flags">
                <span v-if="r.needs_review" class="lit-flag warn">검토</span><span v-if="r.needs_graph_check" class="lit-flag">그래프</span>
                <span v-if="r.is_citation" class="lit-flag">인용</span><span v-if="r.is_model_prediction" class="lit-flag">모델</span>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <div class="search_nodata" v-else-if="table && !loading"><h3>조건에 맞는 행이 없습니다.</h3></div>
        <div class="lit-paging" v-if="table && pages > 1">
          <button type="button" class="sm_btn" :disabled="page <= 1" @click="run(page - 1)">이전</button>
          <span>{{ page }} / {{ pages }}</span>
          <button type="button" class="sm_btn" :disabled="page >= pages" @click="run(page + 1)">다음</button>
        </div>
      </div>

      <!-- 분포 -->
      <div v-else-if="view === 'hist'" class="lit-chart-panel">
        <p v-if="!filter.property" class="lit-note">물성을 하나 고르면 값 분포를 그립니다(위 물성 칩 또는 물성 입력).</p>
        <template v-else-if="hist">
          <div class="lit-chart-head">
            <b>{{ hist.property }}</b> <span v-if="hist.unit">[{{ hist.unit }}]</span> · {{ n(hist.count) }}개
            <span v-if="hist.count"> · 최소 {{ fmt(hist.min) }} · 중앙 {{ fmt(hist.median) }} · 평균 {{ fmt(hist.mean) }} · 최대 {{ fmt(hist.max) }}</span>
            <span v-if="hist.units && hist.units.length > 1" class="lit-dim"> · 단위 혼재: {{ hist.units.map(u => (u.unit || '(없음)') + ' ' + u.count).join(', ') }} — 단위 필터를 쓰세요</span>
          </div>
          <canvas ref="histCanvas" height="110"></canvas>
        </template>
      </div>

      <!-- 산점도 -->
      <div v-else-if="view === 'scatter'" class="lit-chart-panel">
        <div class="lit-axes">
          <label>기본 축
            <select v-model="presetIdx" @change="applyPreset"><option :value="-1">직접 선택</option><option v-for="(a, i) in presets" :key="i" :value="i">{{ a.label }}</option></select>
          </label>
          <label>X <input list="lit-x-list" v-model="axisX" placeholder="물성명 또는 숫자 열(예: annealing_temperature_c)"></label>
          <datalist id="lit-x-list">
            <option v-for="c in numericColumns" :key="'c' + c" :value="c">숫자 열</option>
            <option v-for="p in propertyOptions" :key="'p' + p.property" :value="p.property">물성</option>
          </datalist>
          <label>Y <input list="lit-prop-list" v-model="axisY" placeholder="물성명"></label>
          <button type="button" class="sm_btn" @click="loadScatter">그리기</button>
        </div>
        <div class="lit-chart-head" v-if="scatter">
          {{ n(scatter.count) }}점 · X = {{ scatter.x }}{{ scatter.x_is_column ? ' (열)' : ' (물성)' }} · Y = {{ scatter.y }}
          <span class="lit-dim"> · 같은 논문·시편의 값끼리 짝지음, 인용·모델값 제외. 점을 누르면 논문으로 이동</span>
        </div>
        <canvas ref="scatterCanvas" height="130"></canvas>
      </div>

      <!-- 건수 -->
      <div v-else class="lit-chart-panel">
        <div class="lit-axes">
          <label>기준
            <select v-model="countBy" @change="loadCounts">
              <option value="group">분야</option><option value="institution">학교</option><option value="year">연도(업로드)</option>
              <option value="property">물성</option><option value="sample_type">시편 종류</option><option value="unit">단위</option><option value="status">논문 상태</option>
            </select>
          </label>
        </div>
        <canvas ref="countCanvas" height="110"></canvas>
        <table class="lit-table small" v-if="counts && counts.length">
          <thead><tr><th>{{ countBy }}</th><th>행 수</th><th>논문 수</th></tr></thead>
          <tbody><tr v-for="c in counts" :key="String(c.key)"><td>{{ countLabel(c.key) }}</td><td class="num">{{ n(c.rows_count) }}</td><td class="num">{{ n(c.papers) }}</td></tr></tbody>
        </table>
      </div>

      <p class="lit-error" v-if="error">{{ error }}</p>
    </div>
    <Loading :visible="loading" />
  </div>
</template>

<script>
import Loading from "@/components/common/loading.vue";
import { Chart, BarController, BarElement, ScatterController, PointElement, LinearScale, CategoryScale, Tooltip, Legend } from "chart.js";

Chart.register(BarController, BarElement, ScatterController, PointElement, LinearScale, CategoryScale, Tooltip, Legend);

const PAGE_SIZE = 50;
const NAVY = "#003f67";
const CYAN = "#0dbde9";

function emptyFilter() {
  return { q: "", group: "", property: "", unit: "", valueMin: "", valueMax: "", institution: "", excludeCitation: true, excludeModel: true, needsReview: false, valueOnly: false };
}

export default {
  name: "LitExplore",
  components: { Loading },
  data() {
    return {
      loading: false, exporting: false, error: null,
      stats: null, options: null, groups: [],
      filter: emptyFilter(),
      view: "table", page: 1, table: null,
      hist: null, scatter: null, counts: null, countBy: "group",
      axisX: "", axisY: "", presetIdx: -1,
      charts: {},
    };
  },
  computed: {
    pages() { return this.table ? Math.max(1, Math.ceil(this.table.total / PAGE_SIZE)) : 1; },
    propertyOptions() { return (this.options && this.options.properties) || []; },
    numericColumns() { return (this.options && this.options.numeric_columns) || []; },
    presets() { return (this.options && this.options.preset_axes) || []; },
    unitOptions() {
      const p = this.propertyOptions.find((x) => x.property === this.filter.property);
      if (p && p.units) return String(p.units).split("|").filter(Boolean);
      const all = new Set();
      this.propertyOptions.forEach((x) => String(x.units || "").split("|").filter(Boolean).forEach((u) => all.add(u)));
      return Array.from(all).sort();
    },
  },
  async mounted() {
    const q = this.$route.query || {};
    if (q.property) this.filter.property = String(q.property);
    if (q.group) this.filter.group = String(q.group);
    this.loading = true;
    const [stats, options, groups] = await Promise.all([this.$wApi.lit.stats(), this.$wApi.lit.exploreOptions(), this.$wApi.lit.groups()]);
    this.loading = false;
    if (stats && !stats.error) this.stats = stats;
    if (options && !options.error) this.options = options;
    if (groups && !groups.error) this.groups = groups;
    await this.run(1);
  },
  beforeDestroy() {
    Object.values(this.charts).forEach((c) => c && c.destroy());
  },
  methods: {
    params(extra) {
      const f = this.filter;
      return Object.assign({
        q: f.q || "", group: f.group || "", property: f.property || "", unit: f.unit || "",
        valueMin: f.valueMin === "" ? null : f.valueMin, valueMax: f.valueMax === "" ? null : f.valueMax,
        institution: f.institution || "", excludeCitation: f.excludeCitation ? "1" : "0", excludeModel: f.excludeModel ? "1" : "0",
        needsReview: f.needsReview ? "1" : "0",
      }, extra || {});
    },
    async run(page) {
      this.loading = true; this.error = null;
      this.page = typeof page === "number" ? page : 1;
      const res = await this.$wApi.lit.rowsSearch(this.params({ valueOnly: this.filter.valueOnly ? "1" : "0", page: this.page, pageSize: PAGE_SIZE }));
      this.loading = false;
      if (!res || res.error) { this.error = (res && res.message) || "검색에 실패했습니다."; return; }
      this.table = res;
      if (this.view === "hist") this.loadHist();
      else if (this.view === "count") this.loadCounts();
    },
    reset() { this.filter = emptyFilter(); this.run(1); },
    pickProperty(p) { this.filter.property = this.filter.property === p ? "" : p; this.filter.unit = ""; this.run(1); },
    onPropertyChange() { this.filter.unit = ""; },
    switchView(v) {
      this.view = v;
      this.$nextTick(() => {
        if (v === "hist") this.loadHist();
        else if (v === "count") this.loadCounts();
        else if (v === "scatter" && this.scatter) this.drawScatter();
      });
    },
    async loadHist() {
      if (!this.filter.property) { this.hist = null; return; }
      const res = await this.$wApi.lit.aggregate(this.params({ type: "histogram", bins: 20 }));
      if (!res || res.error) { this.error = (res && res.message) || "분포를 불러오지 못했습니다."; return; }
      this.hist = res;
      this.$nextTick(this.drawHist);
    },
    drawHist() {
      const el = this.$refs.histCanvas;
      if (!el || !this.hist) return;
      this.destroyChart("hist");
      this.charts.hist = new Chart(el, {
        type: "bar",
        data: {
          labels: this.hist.bins.map((b) => `${this.fmt(b.from)}–${this.fmt(b.to)}`),
          datasets: [{ label: `${this.hist.property}${this.hist.unit ? " [" + this.hist.unit + "]" : ""}`, data: this.hist.bins.map((b) => b.count), backgroundColor: NAVY }],
        },
        options: { responsive: true, plugins: { legend: { display: false } }, scales: { x: { ticks: { maxRotation: 60, minRotation: 30, font: { size: 10 } } }, y: { beginAtZero: true, title: { display: true, text: "행 수" } } } },
      });
    },
    applyPreset() {
      const a = this.presets[this.presetIdx];
      if (!a) return;
      this.axisX = a.x; this.axisY = a.y;
      this.loadScatter();
    },
    async loadScatter() {
      if (!this.axisX || !this.axisY) { this.error = "X·Y 축을 정하세요."; return; }
      this.loading = true; this.error = null;
      const res = await this.$wApi.lit.aggregate(this.params({ type: "scatter", x: this.axisX, y: this.axisY, property: "" }));
      this.loading = false;
      if (!res || res.error) { this.error = (res && res.message) || "산점도를 불러오지 못했습니다."; return; }
      this.scatter = res;
      this.$nextTick(this.drawScatter);
    },
    drawScatter() {
      const el = this.$refs.scatterCanvas;
      if (!el || !this.scatter) return;
      this.destroyChart("scatter");
      const pts = this.scatter.points;
      const byGroup = {};
      pts.forEach((p) => { (byGroup[p.group] = byGroup[p.group] || []).push({ x: Number(p.x), y: Number(p.y), meta: p }); });
      const palette = [NAVY, CYAN, "#d9502e", "#2a9d5c", "#7f77dd", "#9aa5b1"];
      const datasets = Object.keys(byGroup).map((g, i) => ({ label: this.groupLabel(g), data: byGroup[g], backgroundColor: palette[i % palette.length], pointRadius: 4 }));
      const self = this;
      this.charts.scatter = new Chart(el, {
        type: "scatter",
        data: { datasets },
        options: {
          responsive: true,
          onClick(evt, elements) {
            if (!elements.length) return;
            const d = datasets[elements[0].datasetIndex].data[elements[0].index];
            if (d && d.meta) self.$router.push("/literature/" + d.meta.paper_id);
          },
          plugins: { tooltip: { callbacks: { label(ctx) { const m = ctx.raw.meta; return `${m.title ? m.title.slice(0, 50) : m.doi} · x=${ctx.raw.x}${m.unit_x ? " " + m.unit_x : ""}, y=${ctx.raw.y}${m.unit_y ? " " + m.unit_y : ""}`; } } } },
          scales: { x: { title: { display: true, text: this.scatter.x } }, y: { title: { display: true, text: this.scatter.y } } },
        },
      });
    },
    async loadCounts() {
      const res = await this.$wApi.lit.aggregate(this.params({ type: "count", by: this.countBy }));
      if (!res || res.error) { this.error = (res && res.message) || "건수를 불러오지 못했습니다."; return; }
      this.counts = res;
      this.$nextTick(this.drawCounts);
    },
    drawCounts() {
      const el = this.$refs.countCanvas;
      if (!el || !this.counts) return;
      this.destroyChart("count");
      const top = this.counts.slice(0, 30);
      this.charts.count = new Chart(el, {
        type: "bar",
        data: { labels: top.map((c) => this.countLabel(c.key)), datasets: [
          { label: "행 수", data: top.map((c) => Number(c.rows_count)), backgroundColor: NAVY },
          { label: "논문 수", data: top.map((c) => Number(c.papers)), backgroundColor: CYAN },
        ] },
        options: { responsive: true, scales: { x: { ticks: { maxRotation: 60, minRotation: 0, font: { size: 10 } } }, y: { beginAtZero: true } } },
      });
    },
    destroyChart(k) { if (this.charts[k]) { this.charts[k].destroy(); this.charts[k] = null; } },
    async exportXlsx() {
      this.exporting = true;
      try {
        const rows = [];
        let page = 1;
        for (;;) {
          const res = await this.$wApi.lit.rowsSearch(this.params({ valueOnly: this.filter.valueOnly ? "1" : "0", page, pageSize: 500 }));
          if (!res || res.error || !res.items.length) break;
          rows.push(...res.items);
          if (rows.length >= res.total || page >= 20) break;
          page += 1;
        }
        const ExcelJS = (await import("exceljs")).default || (await import("exceljs"));
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet("탐색 결과");
        const keys = [];
        rows.forEach((r) => Object.keys(r.data || {}).forEach((k) => { if (!keys.includes(k)) keys.push(k); }));
        ws.columns = [
          { header: "doi", key: "doi", width: 30 }, { header: "title", key: "title", width: 50 }, { header: "group", key: "group", width: 8 },
          { header: "version", key: "version", width: 8 }, { header: "row_no", key: "row_no", width: 7 },
          ...keys.map((k) => ({ header: k, key: k, width: Math.min(40, Math.max(10, k.length + 2)) })),
        ];
        rows.forEach((r) => ws.addRow({ doi: r.doi, title: r.title, group: r.group, version: r.version, row_no: r.row_no, ...(r.data || {}) }));
        const buf = await wb.xlsx.writeBuffer();
        const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `literature_explore_${new Date().toISOString().slice(0, 10)}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
      } catch (e) {
        window.alert("엑셀 내보내기에 실패했습니다: " + (e && e.message ? e.message : e));
      } finally {
        this.exporting = false;
      }
    },
    groupLabel(code) { const g = this.groups.find((x) => x.code === code); return g ? g.label : code; },
    countLabel(key) { return this.countBy === "group" ? this.groupLabel(key) : (key == null ? "(없음)" : String(key)); },
    sampleLabel(r) { const d = r.data || {}; return d.material_name || d.chemical_formula || d.filler_chemistry || ""; },
    condition(r) {
      const d = r.data || {};
      const parts = [];
      ["method", "measurement_method", "temperature_c", "c_rate", "voltage_window_v", "drain_voltage_v", "cycle_number"].forEach((k) => { if (d[k] != null && d[k] !== "") parts.push(`${k}=${d[k]}`); });
      return parts.join(" · ");
    },
    short(s, n) { return s ? (String(s).length > n ? String(s).slice(0, n) + "…" : String(s)) : ""; },
    n(v) { return v == null ? "0" : Number(v).toLocaleString(); },
    fmt(v) { if (v == null) return ""; const x = Number(v); return Math.abs(x) >= 1000 || Math.abs(x) < 0.01 && x !== 0 ? x.toPrecision(3) : x.toFixed(2).replace(/\.?0+$/, ""); },
  },
};
</script>

<style scoped>
.lit-back { display: inline-block; margin-bottom: 10px; color: #0c78b3; font-size: 13px; }
.lit-cards { display: grid; grid-template-columns: repeat(3, 160px) 1fr; gap: 10px; margin-bottom: 14px; }
.lit-card { border: 1px solid #dde3ea; border-radius: 8px; padding: 10px 12px; background: #fff; }
.lit-card .lab { font-size: 12px; color: #778; }
.lit-card .val { font-size: 22px; font-weight: 700; color: #003f67; }
.lit-card .sub { font-size: 11px; color: #889; }
.lit-card.wide .props { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 4px; }
.lit-chip { border: 1px solid #cfd8e3; background: #fff; border-radius: 999px; padding: 3px 9px; font-size: 12px; color: #334; cursor: pointer; }
.lit-chip b { margin-left: 3px; color: #003f67; }
.lit-chip.on { background: #003f67; border-color: #003f67; color: #fff; }
.lit-chip.on b { color: #fff; }
.lit-filter { display: flex; flex-wrap: wrap; gap: 10px 14px; align-items: flex-end; padding: 10px 12px; border: 1px solid #dde3ea; border-radius: 8px; background: #f8fafc; margin-bottom: 12px; font-size: 12.5px; }
.lit-filter label { display: flex; flex-direction: column; gap: 3px; color: #556; }
.lit-filter input, .lit-filter select { height: 30px; border: 1px solid #cfd8e3; border-radius: 4px; padding: 0 8px; font-size: 12.5px; background: #fff; min-width: 120px; }
.lit-filter input.num { min-width: 80px; width: 90px; }
.lit-filter .lit-check { flex-direction: row; align-items: center; gap: 4px; }
.lit-tabs { display: flex; gap: 4px; border-bottom: 1px solid #dde3ea; margin-bottom: 12px; align-items: center; }
.lit-tab { border: none; background: none; padding: 8px 14px; font-size: 14px; color: #667; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -1px; }
.lit-tab.on { color: #003f67; border-bottom-color: #003f67; font-weight: 600; }
.lit-tab small { color: #99a; font-weight: 400; }
.lit-tabs-right { margin-left: auto; }
.lit-table-wrap { overflow-x: auto; border-top: 2px solid #003f67; }
.lit-table { width: 100%; border-collapse: collapse; font-size: 12.5px; min-width: 1100px; }
.lit-table.small { min-width: 0; width: auto; margin-top: 12px; border-top: 1px solid #dde3ea; }
.lit-table th { background: #f4f7fa; padding: 8px 7px; text-align: left; border-bottom: 1px solid #dde3ea; font-weight: 600; color: #223; white-space: nowrap; }
.lit-table td { padding: 7px; border-bottom: 1px solid #eef1f5; vertical-align: top; color: #333; }
.lit-table tr.review td { background: #fffbf0; }
.lit-table a { color: #223; }
.lit-table a:hover { text-decoration: underline; }
.paper { max-width: 260px; }
.quote { max-width: 380px; color: #445; }
.lit-sub { font-size: 11px; color: #778; }
.mono { font-family: Consolas, "Courier New", monospace; font-size: 11.5px; }
.num { text-align: right; white-space: nowrap; }
.flags { white-space: nowrap; }
.lit-flag { display: inline-block; padding: 1px 6px; border-radius: 999px; font-size: 10.5px; background: #eef1f5; color: #556; margin-right: 3px; }
.lit-flag.warn { background: #fff0c2; color: #7a5200; }
.lit-paging { display: flex; gap: 12px; align-items: center; justify-content: center; margin: 16px 0; color: #556; font-size: 13px; }
.lit-chart-panel { font-size: 13px; color: #334; }
.lit-chart-head { margin-bottom: 8px; }
.lit-axes { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; margin-bottom: 10px; font-size: 12.5px; }
.lit-axes label { display: flex; flex-direction: column; gap: 3px; color: #556; }
.lit-axes input, .lit-axes select { height: 30px; border: 1px solid #cfd8e3; border-radius: 4px; padding: 0 8px; min-width: 240px; font-size: 12.5px; }
.lit-note { color: #667; }
.lit-dim { color: #99a; }
.lit-error { color: #b23; margin-top: 10px; }
</style>
